import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import LEOfferPriceDetails from './LEOfferPriceDetails'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isUltraLuxOffer } from 'lib/offer/offerUtils'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import LuxPlusHiddenOfferCarouselCard from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferCarouselCard'
import { connect } from 'react-redux'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { isFlashHotelOffer, isHotelPackage } from 'lib/offer/offerTypes'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import Group from 'components/utils/Group'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { take } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import LoyaltyStayUpgradeBenefitPill from 'loyalty/components/LoyaltyStayUpgradeBenefitPill'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import GeoContext from 'contexts/geoContext'
import ContentTagLabels from '../../Content/ContentTagLabels'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'

interface MappedProps {
  isLuxPlusEnabled: boolean
  isLuxPlusHiddenOffer: boolean
  inclusionsViewableLuxPlusTier: App.MembershipTier
}

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends MappedProps, InheritedCarouselCardMediumProps {
  offer: App.HotelOffer | App.HotelOfferSummary
  onClick?: () => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function LEOfferCard(props: Props) {
  const intl = useIntl()
  const {
    offer,
    size,
    onClick,
    className,
    offerUrl,
    isLuxPlusHiddenOffer,
    isLuxPlusEnabled,
    selectedDatesAndRoomsFilter,
    inclusionsViewableLuxPlusTier,
    variant,
    eagerLoadImage,
  } = props
  const { lowestPricePackage } = offer
  const isUltraLux = isUltraLuxOffer(offer)
  const memberPrice = lowestPricePackage?.memberPrice ?? 0
  const showMemberPrice = isLuxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (lowestPricePackage?.price ?? 0) - memberPrice : undefined
  const luxPlusInclusions = lowestPricePackage?.luxPlusInclusionsByTier?.[inclusionsViewableLuxPlusTier as App.MembershipSubscriptionTier]
  const offerLabels = useMemo(() => offer.urgencyTags.filter(label => label.type !== 'left'), [offer])

  const isFlashHotel = isFlashHotelOffer(offer)
  const inclusionsNoMarketingTaglineVariant = useOptimizelyExperiment(OptimizelyExperiments.croInclusionsButNoMarketingTaglineFlashTiles)
  const isInclusionsNoMarketingTaglineEnabled = getOptimizelyEnabledDecision(inclusionsNoMarketingTaglineVariant)
  const shouldShowFlashInclusions = isFlashHotel

  const bestPkg = useOffersBestPrices(offer, selectedDatesAndRoomsFilter || {})
  const pkg = bestPkg?.bestPricePackage ?? offer.lowestPricePackage!

  const inclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.tileInclusions?.length) {
      return offer.tileInclusions
    }
    if (offer.lowestPricePackage?.inclusions?.length) {
      return offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate')
    }
    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    return pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate') || []
  }, [offer])

  const shortInclusions = useMemo(() => take(inclusions, 3), [inclusions])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (offer.bundledWithFlightsOnly) {
      return []
    }
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        pkg: isHotelPackage(pkg) ? pkg : undefined,
        price: pkg.price,
        memberPrice: pkg.memberPrice,
        currentRegionCode,
        nights: pkg.duration,
      }),
    ]
  }, [offer, pkg, currentRegionCode])

  const hasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')
  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  // Check if this is a charter bundle offer
  const isCharterBundleOffer = useMemo(() => {
    return isFlashHotelOffer(offer) &&
      offer.offerFlightsEnabled &&
      offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
  }, [offer])

  const charterBundleImageLabel = isCharterBundleOffer
    ? (
        <StructureLabel variant="maldives-charter">
          {intl.formatMessage({
            id: 'PjmdhR5JAQXM7v4WOevW',
            defaultMessage: 'Hotel & Flight bundle',
            description: 'Label for charter bundle offers combining hotel and flight',
          })}
        </StructureLabel>
      )
    : undefined

  let urgencyTagLabel: React.ReactElement | undefined = undefined
  const urgencyTagAlreadyActive = offerLabels.find((ol: App.OfferUrgencyLabel) => ol.type == 'left' && ol.end || ol.type == 'lux_plus_general_release')
  if (offer?.visibilitySchedule?.end && !urgencyTagAlreadyActive) {
    urgencyTagLabel = <OfferUrgencyCountdownLabel
      endDate={offer?.visibilitySchedule?.end}
      tagPlace="offer-card"
      fallback={size === 'medium' && isUltraLux ? <ProductTypeLabel productType={offer.productType} /> : undefined}
    />
  }

  const hasInclusions = shortInclusions.length > 0
  const remainingInclusionsCount = inclusions.length - shortInclusions.length
  const showInclusionsInsteadOfTagline = isInclusionsNoMarketingTaglineEnabled && hasInclusions

  const inclusionsElement = (shouldShowFlashInclusions && hasInclusions) ? <Group
    gap={8}
    tabletGap={8}
    direction="vertical"
    tabletDirection="horizontal"
    horizontalAlign="start"
    wrap="wrap"
    style={{ paddingBottom: 8 }}
  >
    {shortInclusions.map((inclusion) => <OfferInclusionItem
      key={inclusion.id}
      inclusion={inclusion}
      size="medium"
      isHighlighted={false}
    />)}
    {remainingInclusionsCount > 0 && (
      <BodyText variant="medium" underline>
        {intl.formatMessage(
          {
            id: '-_Y7PlvvzQaDdQJ6J66z',
            defaultMessage: '+{count} more',
            description: 'Text indicating additional inclusions count on offer card',
          },
          { count: remainingInclusionsCount },
        )}
      </BodyText>
    )}
  </Group> : undefined
  const flashTileDescription = showInclusionsInsteadOfTagline ? undefined : offer.name

  return (
    <ProductPaletteProvider product={offer}>
      {isLuxPlusHiddenOffer && <LuxPlusHiddenOfferCarouselCard urgencyLabels={offerLabels} offer={offer} size={size} />}
      {!isLuxPlusHiddenOffer && <>
        {size === 'medium' && <CarouselCardMedium
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={[offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')}
          providerName={offer.property.name}
          title={showInclusionsInsteadOfTagline ? '' : offer.name}
          priceDetails={<LEOfferPriceDetails variant="condensed" offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} />}
          to={offerUrl}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          imageCornerLabel={charterBundleImageLabel}
          urgencyLabels={<LabelGroup>
            <ContentTagLabels offerId={offer.id} page="search" />
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
            {urgencyTagLabel}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          taglineInclusions={inclusionsElement}
          titleVariant={showInclusionsInsteadOfTagline ? 'medium' : 'large'}
          onClick={onClick}
        />}
        {size === 'small' && <CarouselCardSmall
          variant={variant}
          data-testid="offer-card"
          className={className}
          bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
          description={flashTileDescription}
          image={offer.image}
          eagerLoadImage={eagerLoadImage}
          images={offer.images}
          location={[offer.locationHeading, offer.locationSubheading].filter((t) => t).join(', ')}
          title={offer.property.name}
          priceDetails={<LEOfferPriceDetails offer={offer} selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter} />}
          to={offerUrl}
          productType={isCharterBundleOffer ? undefined : offer.productType}
          rating={isOfferRatingDisplayable(offer.property.rating) ? offer.property.rating : undefined}
          partnershipLabel={<OfferPartnershipLabel offer={offer} />}
          imageCornerLabel={charterBundleImageLabel}
          urgencyLabels={<LabelGroup>
            <ContentTagLabels offerId={offer.id} page="search" />
            <OfferLabels
              offer={offer}
              urgencyLabels={offerLabels}
              saveAmount={saveAmount}
              showMemberInclusionLabels
              luxPlusInclusions={luxPlusInclusions}
              leadWithLuxPlusPrice
            />
            {urgencyTagLabel}
            {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
          </LabelGroup>}
          loyaltyPointsElement={<LoyaltyPointsEarn
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />}
          loyaltyPackageUpgradeElement={(hasUpgradablePackages && hasRoomUpgradesAvailable) ? <LoyaltyStayUpgradeBenefitPill offer={offer} /> : undefined}
          taglineInclusions={inclusionsElement}
          onClick={onClick}
        />}
      </>}
    </ProductPaletteProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>): MappedProps {
  return ({
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLuxPlusHiddenOffer: getIsLuxPlusHiddenOffer(state, ownProps.offer),
    inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(state),
  })
}

export default connect(mapStateToProps)(LEOfferCard)
